import { useMemo, useState } from "react";
import { Modal } from "./Modal";
import { api, ApiError } from "../lib/api";
import { useResource } from "../lib/hooks";

/**
 * The service charge editor — where "what to charge" is set and seen.
 *
 * A service is either an NDIS hourly support (billed under the cap) or a
 * non-hourly line (flat item, per-km travel, cancellation). For the NDIS case
 * you pick HOW it's billed:
 *   - a nursing role (EN/RN/CN/CNC/NP) + budget group (Core 01 / Capacity 15),
 *     which resolves the right day-typed caps, or
 *   - a single fixed item number.
 * You set your charge as a % of the cap (default 88), and the live waterfall
 * shows cap -> your charge -> provider keeps -> reference pay -> on-costs ->
 * NET margin, so gross is never mistaken for profit. Charge can't exceed 100%
 * of cap.
 *
 * The pay rate + employment type in the waterfall are a PREVIEW sanity-check
 * (real pay comes from the worker's record at invoice time); they aren't saved.
 * For hourly services the computed weekday charge is stored as default_rate so
 * quotes still prefill from it.
 */

interface NdisItem {
    item_number: string;
    name: string;
    billing_role: string | null;
    day_type: string | null;
    reg_group: string | null;
    price_national: string | null;
}
interface RoleRow { id: string; name: string; base_hourly_rate: string | null; }
interface OncostRow { employment_type: string; loading_percent: string; }

export interface ServiceRow {
    id?: string;
    name?: string;
    category?: string | null;
    description?: string | null;
    unit?: string | null;
    default_rate?: string | number | null;
    line_type?: string | null;
    billing_role?: string | null;
    ndis_reg_prefix?: string | null;
    ndis_item_number?: string | null;
    charge_percent?: string | number | null;
    gst_free?: boolean;
    sort_order?: number | null;
    is_active?: boolean;
}

const BILLING_ROLES = [
    { code: "EN", label: "Enrolled Nurse (EN)" },
    { code: "RN", label: "Registered Nurse (RN)" },
    { code: "CN", label: "Clinical Nurse (CN)" },
    { code: "CNC", label: "Clinical Nurse Consultant (CNC)" },
    { code: "NP", label: "Nurse Practitioner (NP)" },
];
const REG_GROUPS = [
    { value: "01", label: "Core — Assistance with Daily Life" },
    { value: "15", label: "Capacity Building — Improved Daily Living" },
];
const EMP_TYPES = [
    { value: "casual", label: "Casual" }, { value: "part_time", label: "Part-time" },
    { value: "full_time", label: "Full-time" }, { value: "contractor", label: "Contractor (ABN)" },
];

const currency = (n: number | null): string =>
    n == null || Number.isNaN(n) ? "—" : n.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
const num = (v: unknown): number => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

export function ServiceEditor({ service, onClose, onSaved }: {
    service: ServiceRow | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const items = useResource<NdisItem[]>("/ndis-items?limit=1000");
    const roles = useResource<RoleRow[]>("/roles");
    const oncosts = useResource<OncostRow[]>("/oncost-loadings");

    const [name, setName] = useState(service?.name ?? "");
    const [category, setCategory] = useState(service?.category ?? "");
    const [unit, setUnit] = useState(service?.unit ?? "hour");
    const [description, setDescription] = useState(service?.description ?? "");
    const [lineType, setLineType] = useState(service?.line_type ?? "hourly");
    const [billingRole, setBillingRole] = useState(service?.billing_role ?? "");
    const [regPrefix, setRegPrefix] = useState(service?.ndis_reg_prefix ?? "01");
    const [itemNumber, setItemNumber] = useState(service?.ndis_item_number ?? "");
    const [chargePercent, setChargePercent] = useState(
        service?.charge_percent != null ? String(Number(service.charge_percent)) : "88");
    const [gstFree, setGstFree] = useState(service?.gst_free ?? true);
    const [flatRate, setFlatRate] = useState(
        service?.default_rate != null ? String(Number(service.default_rate)) : "");
    const [isActive, setIsActive] = useState(service?.is_active ?? true);

    // Preview-only inputs for the margin check (not saved).
    const [empType, setEmpType] = useState("casual");
    const [refPay, setRefPay] = useState("");

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isHourly = lineType === "hourly";
    const usingRole = isHourly && !!billingRole;
    const pct = num(chargePercent);

    // Resolve the caps for the current selection. For a nursing role, that's the
    // 6 day-typed caps (matching billing_role + budget-group prefix). For a fixed
    // item, the single national cap.
    const capsByDay = useMemo(() => {
        const map = new Map<string, number>();
        if (!isHourly) return map;
        const rows = items.data ?? [];
        if (usingRole) {
            for (const it of rows) {
                if (it.billing_role === billingRole && it.item_number.startsWith(regPrefix) &&
                    it.day_type && it.price_national != null) {
                    map.set(it.day_type, num(it.price_national));
                }
            }
        } else if (itemNumber) {
            const it = rows.find((r) => r.item_number === itemNumber.trim());
            if (it?.price_national != null) map.set("weekday_day", num(it.price_national));
        }
        return map;
    }, [items.data, isHourly, usingRole, billingRole, regPrefix, itemNumber]);

    const refCap = capsByDay.get("weekday_day") ?? null;

    // Prefill the reference pay from the matching job-role's base rate, once.
    const suggestedPay = useMemo(() => {
        if (!usingRole) return null;
        const match = (roles.data ?? []).find((r) => r.name.includes(`(${billingRole})`));
        return match?.base_hourly_rate != null ? num(match.base_hourly_rate) : null;
    }, [roles.data, usingRole, billingRole]);

    const loadingPct = num((oncosts.data ?? []).find((o) => o.employment_type === empType)?.loading_percent);

    // The waterfall, at the weekday reference cap.
    const pay = refPay !== "" ? num(refPay) : (suggestedPay ?? 0);
    const charge = isHourly ? (refCap != null ? refCap * (pct / 100) : null) : num(flatRate) || null;
    const providerKeeps = isHourly && refCap != null && charge != null ? refCap - charge : null;
    const oncostAmt = pay * (loadingPct / 100);
    const grossMargin = charge != null ? charge - pay : null;
    const netMargin = charge != null ? charge - pay - oncostAmt : null;

    const chargeTooHigh = pct > 100;

    const save = async () => {
        if (!name.trim()) { setError("A service name is required."); return; }
        if (chargeTooHigh) { setError("Your charge can't exceed 100% of the cap."); return; }
        setBusy(true); setError(null);
        try {
            const body: Record<string, unknown> = {
                name: name.trim(),
                category: category.trim() || null,
                description: description.trim() || null,
                unit: unit.trim() || "hour",
                line_type: lineType,
                gst_free: gstFree,
                is_active: isActive,
            };
            if (isHourly) {
                body.charge_percent = pct;
                body.billing_role = usingRole ? billingRole : null;
                body.ndis_reg_prefix = usingRole ? regPrefix : null;
                body.ndis_item_number = usingRole ? null : (itemNumber.trim() || null);
                // Store the weekday charge so quote lines still prefill a rate.
                body.default_rate = charge != null ? Math.round(charge * 100) / 100 : null;
            } else {
                body.billing_role = null;
                body.ndis_reg_prefix = null;
                body.ndis_item_number = null;
                body.charge_percent = null;
                body.default_rate = num(flatRate) || null;
            }
            if (service?.id) await api.put(`/services/${service.id}`, body);
            else await api.post("/services", body);
            onSaved();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Could not save the service");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title={service?.id ? `Edit ${service.name ?? "service"}` : "New service"}
            width="wide"
            onClose={onClose}
            footer={
                <>
                    <button className="btn" disabled={busy} onClick={() => void save()}>
                        {busy ? "Saving…" : service?.id ? "Save changes" : "Create service"}
                    </button>
                    <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
                </>
            }
        >
            {error && <div className="error-banner">{error}</div>}

            <div className="form-grid">
                <div className="field half">
                    <label>Service name <span className="req">*</span></label>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="RN nursing support" />
                </div>
                <div className="field half">
                    <label>Category</label>
                    <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Nursing" />
                </div>
                <div className="field half">
                    <label>How it's billed</label>
                    <select value={lineType} onChange={(e) => setLineType(e.target.value)}>
                        <option value="hourly">Hourly — NDIS support (cap × your %)</option>
                        <option value="flat">Flat item (e.g. assessment, care plan)</option>
                        <option value="km">Per-km travel</option>
                        <option value="cancellation">Cancellation</option>
                    </select>
                </div>
                <div className="field half">
                    <label>Unit</label>
                    <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="hour / km / each" />
                </div>
            </div>

            {isHourly ? (
                <>
                    <h3 style={{ margin: "1rem 0 0.4rem" }}>NDIS billing</h3>
                    <div className="form-grid">
                        <div className="field half">
                            <label>Nursing role (day-typed caps)</label>
                            <select value={billingRole} onChange={(e) => setBillingRole(e.target.value)}>
                                <option value="">— use a fixed item instead —</option>
                                {BILLING_ROLES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                            </select>
                        </div>
                        {usingRole ? (
                            <div className="field half">
                                <label>Budget group</label>
                                <select value={regPrefix} onChange={(e) => setRegPrefix(e.target.value)}>
                                    {REG_GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                                </select>
                            </div>
                        ) : (
                            <div className="field half">
                                <label>Fixed NDIS item number</label>
                                <input value={itemNumber} onChange={(e) => setItemNumber(e.target.value)}
                                    placeholder="e.g. 01_606_0114_1_1" className="mono" />
                            </div>
                        )}
                        <div className="field half">
                            <label>Your charge (% of cap)</label>
                            <input type="number" min={0} max={100} step="1" value={chargePercent}
                                onChange={(e) => setChargePercent(e.target.value)} />
                            {chargeTooHigh && <p className="help" style={{ color: "var(--danger)" }}>Can't exceed 100% — you'd bill above the NDIS cap.</p>}
                        </div>
                        <div className="field half">
                            <label className="check" style={{ marginTop: "1.6rem" }}>
                                <input type="checkbox" checked={gstFree} onChange={(e) => setGstFree(e.target.checked)} />{" "}
                                GST-free (most NDIS supports)
                            </label>
                        </div>
                    </div>

                    {/* The waterfall */}
                    <h3 style={{ margin: "1rem 0 0.4rem" }}>What everyone gets — at the weekday cap</h3>
                    {refCap == null ? (
                        <p className="help">Pick a nursing role or enter a valid item number to see the caps.</p>
                    ) : (
                        <>
                            <div className="quote-totals" style={{ maxWidth: 420 }}>
                                <Row label={`NDIS cap (weekday${usingRole ? ", per hour" : ""})`} value={currency(refCap)} />
                                <Row label={`Your charge (${pct || 0}% of cap)`} value={currency(charge)} strong />
                                <Row label="→ Provider keeps" value={currency(providerKeeps)} muted />
                                <div style={{ borderTop: "1px solid var(--line)", margin: "0.4rem 0" }} />
                                <div className="form-grid" style={{ gap: "0.4rem", marginBottom: "0.4rem" }}>
                                    <div className="field half" style={{ margin: 0 }}>
                                        <label style={{ fontSize: "0.72rem" }}>Reference pay ($/hr)</label>
                                        <input type="number" min={0} step="0.01"
                                            value={refPay} placeholder={suggestedPay != null ? String(suggestedPay) : "0"}
                                            onChange={(e) => setRefPay(e.target.value)} />
                                    </div>
                                    <div className="field half" style={{ margin: 0 }}>
                                        <label style={{ fontSize: "0.72rem" }}>Worker type (on-cost)</label>
                                        <select value={empType} onChange={(e) => setEmpType(e.target.value)}>
                                            {EMP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <Row label={`→ Worker pay`} value={currency(pay)} muted />
                                <Row label={`→ On-costs (${loadingPct || 0}% of pay)`} value={currency(oncostAmt)} muted />
                                <div style={{ borderTop: "1px solid var(--line)", margin: "0.4rem 0" }} />
                                <Row label="Gross margin" value={currency(grossMargin)} />
                                <Row label="Net margin (after on-costs)" value={currency(netMargin)} strong
                                    danger={netMargin != null && netMargin < 0} />
                            </div>
                            {netMargin != null && netMargin < 0 &&
                                <p className="help" style={{ color: "var(--danger)" }}>Net margin is negative — your charge doesn't cover pay + on-costs.</p>}
                            <p className="help" style={{ marginTop: 6 }}>
                                Evening, night, weekend and public-holiday shifts bill at their own (higher) caps automatically —
                                see the full set on the NDIS Pricing page. Pay/worker-type here are a preview only.
                            </p>
                        </>
                    )}
                </>
            ) : (
                <>
                    <h3 style={{ margin: "1rem 0 0.4rem" }}>Rate</h3>
                    <div className="form-grid">
                        <div className="field half">
                            <label>{lineType === "km" ? "Rate per km ($)" : "Flat rate ($)"}</label>
                            <input type="number" min={0} step="0.01" value={flatRate}
                                onChange={(e) => setFlatRate(e.target.value)} placeholder={lineType === "km" ? "0.99" : "390.00"} />
                        </div>
                        <div className="field half">
                            <label className="check" style={{ marginTop: "1.6rem" }}>
                                <input type="checkbox" checked={gstFree} onChange={(e) => setGstFree(e.target.checked)} />{" "}
                                GST-free
                            </label>
                        </div>
                    </div>
                    <p className="help">Non-NDIS / off-cap line — billed at the flat rate you set, not a cap percentage.</p>
                </>
            )}

            <div className="field" style={{ marginTop: "1rem" }}>
                <label>Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <label className="check">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
            </label>
        </Modal>
    );
}

/** One line in the waterfall block. */
function Row({ label, value, strong, muted, danger }: {
    label: string; value: string; strong?: boolean; muted?: boolean; danger?: boolean;
}) {
    return (
        <div style={{
            display: "flex", justifyContent: "space-between",
            fontWeight: strong ? 700 : 400,
            color: danger ? "var(--danger)" : muted ? "var(--muted)" : undefined,
            padding: "2px 0",
        }}>
            <span>{label}</span><span className="mono">{value}</span>
        </div>
    );
}
